// Which screen of the admin is open (SPEC-34): the base, or one athlete's
// profile at ?utilizadora={id} on the admin's own path, /admin or any path
// of an admin.* host. The address is the state, so a profile's link can be
// shared in the team and the browser's back returns to the base.
//
// The app's router (src/lib/router.tsx) reads the path only; the admin owns
// its query, on the same History API.

import { useCallback, useEffect, useState } from "react";
import type { MouseEvent } from "react";

const PARAM = "utilizadora";

/** Set on the entry a profile pushes from the base, so the way back goes down the history. */
const FROM_BASE = "pyfAdminFromBase";

/** The athlete a query names, or null for the base. */
function accountIdIn(search: string): string | null {
  const accountId = new URLSearchParams(search).get(PARAM);
  return accountId === null || accountId.trim() === "" ? null : accountId;
}

/** Whether the address opens a profile rather than the base (SPEC-43: which skeleton to draw). */
export const profileInAddress = (search: string = window.location.search): boolean => accountIdIn(search) !== null;

/** A profile's address, relative to the admin's path: « ?utilizadora=ma ». */
export const profileHref = (accountId: string) => `?${new URLSearchParams({ [PARAM]: accountId }).toString()}`;

/** The base's address: the admin's path, without the query. */
export const baseHref = () => window.location.pathname;

function pushedFromBase(): boolean {
  const state: unknown = window.history.state;
  return typeof state === "object" && state !== null && FROM_BASE in state;
}

/**
 * The open screen, and the ways between the two. Opening a profile pushes
 * an entry; the way back goes down the history when the base is the entry
 * below, so the history holds it once, and pushes the base otherwise (a
 * profile opened from a shared link).
 */
export function useAdminRoute() {
  const [accountId, setAccountId] = useState(() => accountIdIn(window.location.search));

  useEffect(() => {
    const onPopState = () => setAccountId(accountIdIn(window.location.search));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const openProfile = useCallback((id: string) => {
    window.history.pushState({ [FROM_BASE]: true }, "", profileHref(id));
    setAccountId(id);
  }, []);

  const openBase = useCallback(() => {
    if (pushedFromBase()) {
      window.history.back();
      return;
    }
    window.history.pushState(null, "", baseHref());
    setAccountId(null);
  }, []);

  return { accountId, openProfile, openBase };
}

/** A click with the main button and no modifier key: one the page may handle in place. */
export function plainClick(event: MouseEvent): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

/** A link's plain click, handled in place; any other keeps the browser's own behaviour, a new tab or window. */
export function inPlace(action: () => void) {
  return (event: MouseEvent<HTMLAnchorElement>) => {
    if (!plainClick(event)) return;
    event.preventDefault();
    action();
  };
}
